import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  NotificationPagination,
  Notification,
  NotificationPreferencesType,
  NotificationStatistic,
} from '@/types/notification';

export type NotificationState = {
  items: Notification[];
  notifications: Notification[];
  unreadCount: number;
  statisticUnreadCount: number;
  pagination: NotificationPagination;
  error: string | null;
  dataNotiPreferences?: NotificationPreferencesType;
};

const notificationPaginationInitialState: NotificationPagination = {
  pageNumber: 1,
  pageSize: 10,
  totalElements: 0,
  totalPages: 0,
  numberOfElements: 0,
  first: true,
  last: true,
};

export const notificationInitialState: NotificationState = {
  items: [],
  notifications: [],
  unreadCount: 0,
  statisticUnreadCount: 0,
  pagination: notificationPaginationInitialState,
  error: null,
};

const syncNotificationLists = (state: NotificationState, list: Notification[]) => {
  state.items = list;
  state.notifications = list;
};

const slice = createSlice({
  name: 'notification',
  initialState: notificationInitialState,
  reducers: {
    setNotificationsAction(
      state,
      {
        payload,
      }: PayloadAction<{
        notifications: Notification[];
        unreadCount?: number;
        pagination?: NotificationPagination;
      }>,
    ) {
      const sorted = [...payload.notifications].sort(
        (left, right) =>
          new Date(right.createdAtUtc ?? 0).getTime() - new Date(left.createdAtUtc ?? 0).getTime(),
      );
      syncNotificationLists(state, sorted);
      if (payload.pagination) {
        state.pagination = {
          ...notificationPaginationInitialState,
          ...payload.pagination,
        };
      }
      state.unreadCount = payload.unreadCount ?? sorted.filter((notification) => !notification.isRead).length;
    },
    setNotificationStatisticAction(state, { payload }: PayloadAction<NotificationStatistic>) {
      state.statisticUnreadCount = payload.unreadCount ?? 0;
    },
    prependNotificationAction(state, { payload }: PayloadAction<Notification>) {
      const items = [payload, ...state.items.filter((item) => item.id !== payload.id)].sort(
        (a, b) => new Date(b.createdAtUtc).getTime() - new Date(a.createdAtUtc).getTime(),
      );

      syncNotificationLists(state, items);
      state.unreadCount = items.filter((item) => !item.isRead).length;
      state.statisticUnreadCount = state.unreadCount;
    },
    markNotificationsReadAction(
      state,
      {
        payload,
      }: PayloadAction<string[] | { notificationIds: string[]; readAtUtc?: string | null }>,
    ) {
      const notificationIds = Array.isArray(payload) ? payload : payload.notificationIds;
      const readAtUtc = Array.isArray(payload)
        ? new Date().toISOString()
        : (payload.readAtUtc ?? new Date().toISOString());
      const idSet = new Set(notificationIds);
      const items = state.items.map((notification) => {
        if (!idSet.has(notification.id)) return notification;

        return {
          ...notification,
          isRead: true,
          readAtUtc,
        };
      });

      syncNotificationLists(state, items);
      state.unreadCount = items.filter((notification) => !notification.isRead).length;
      state.statisticUnreadCount = state.unreadCount;
    },
    markAllNotificationsReadAction(state) {
      const readAtUtc = new Date().toISOString();
      const items = state.items.map((notification) => ({
        ...notification,
        isRead: true,
        readAtUtc: notification.readAtUtc ?? readAtUtc,
      }));

      syncNotificationLists(state, items);
      state.unreadCount = 0;
      state.statisticUnreadCount = 0;
    },
    setNotificationsErrorAction(state, { payload }: PayloadAction<string | null>) {
      state.error = payload;
    },
    updateNotificationAction(state, { payload }: PayloadAction<Notification>) {
      state.notifications = state.notifications.map((item) =>
        item.id === payload.id ? payload : item,
      );
      state.notifications.sort(
        (left, right) =>
          new Date(right.createdAtUtc ?? 0).getTime() - new Date(left.createdAtUtc ?? 0).getTime(),
      );
      state.unreadCount = state.notifications.filter((item) => !item.isRead).length;
      state.statisticUnreadCount = state.unreadCount;
    },
    removeNotificationAction(state, { payload }: PayloadAction<{ id: string }>) {
      state.notifications = state.notifications.filter((item) => item.id !== payload.id);
      state.unreadCount = state.notifications.filter((item) => !item.isRead).length;
      state.statisticUnreadCount = state.unreadCount;
    },
    removeNotificationsAction(state, { payload }: PayloadAction<{ ids: string[] }>) {
      state.notifications = state.notifications.filter((item) => !payload.ids.includes(item.id));
      state.unreadCount = state.notifications.filter((item) => !item.isRead).length;
      state.statisticUnreadCount = state.unreadCount;
    },

    setNotificationPreferencesAction(
      state,
      { payload }: PayloadAction<{ dataNotiPreferences: NotificationPreferencesType }>,
    ) {
      state.dataNotiPreferences = payload.dataNotiPreferences;
    },
  },
});

export const {
  markAllNotificationsReadAction,
  updateNotificationAction,
  removeNotificationAction,
  removeNotificationsAction,
  setNotificationPreferencesAction,
  markNotificationsReadAction,
  prependNotificationAction,
  setNotificationStatisticAction,
  setNotificationsAction,
  setNotificationsErrorAction,
} = slice.actions;

export default slice.reducer;
